import { useState } from "react";
import { BellRing, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useVault } from "@/context/VaultContext";

const HIDE_KEY = "aph_alerts_prompt_until";
const WEEK = 7 * 24 * 3600 * 1000;

const shouldShow = (supported, profile) => {
  if (!supported) return false;
  if (Notification.permission === "granted" && profile.push_on) return false;
  return Number(localStorage.getItem(HIDE_KEY) || 0) < Date.now();
};

export function AlertsPrompt() {
  const { pushSupported, enablePush, profile } = useVault();
  const [hidden, setHidden] = useState(false);
  const [, refresh] = useState(0);
  const [busy, setBusy] = useState(false);
  if (hidden || !shouldShow(pushSupported(), profile)) return null;
  const blocked = Notification.permission === "denied";

  const hide = () => { localStorage.setItem(HIDE_KEY, String(Date.now() + WEEK)); setHidden(true); };
  const enable = async () => {
    setBusy(true);
    try {
      await enablePush();
      toast.success("Lock-screen alerts on — content is never included");
      setHidden(true);
    } catch (e) {
      toast.error(Notification.permission === "denied" ? "Alerts are blocked. Allow notifications for Aphelion in your browser or phone settings." : e.message);
    } finally {
      setBusy(false);
      refresh((n) => n + 1);
    }
  };

  return (
    <div className="mx-4 mb-2 bg-nebula border border-stellar/30 rounded-secure p-3 flex items-start gap-3 rise-in" data-testid="alerts-prompt">
      <BellRing size={20} strokeWidth={1.5} className="text-stellar flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" data-testid="alerts-prompt-title">{blocked ? "Lock-screen alerts are blocked" : "Turn on lock-screen alerts"}</p>
        <p className="text-xs text-ash/55 mt-0.5">{blocked ? "Allow notifications for Aphelion in your browser's site settings (tap the icon left of the address), then try again." : "Know when a message or Snap arrives. Alerts only say \"New secure message\"."}</p>
        <button onClick={enable} disabled={busy} data-testid="alerts-prompt-enable-btn" className="mt-2.5 flex items-center gap-1.5 bg-stellar text-void font-branding font-semibold text-xs rounded-secure px-3 py-1.5 hover:opacity-85 transition-opacity disabled:opacity-60">
          {busy && <Loader2 size={12} className="animate-spin" />} {blocked ? "Try again" : "Turn on"}
        </button>
      </div>
      <button onClick={hide} data-testid="alerts-prompt-dismiss-btn" aria-label="Not now" className="text-ash/40 hover:text-ash p-0.5"><X size={16} strokeWidth={1.5} /></button>
    </div>
  );
}
